import { useEffect, useRef, useState } from "react";
import HeaderLogo from "../components/HeaderLogo";
import PageName from "../components/PageName";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import {
  enRatio,
  fetchAuthPost,
  hhmm_to_number_sec,
  timeToYYYYMM,
  timeToYYYYMMDD,
  yyyymmddd_ddmmyyyy_v1,
} from "../functions/functions";
import api from "../routes/api";
import Loading from "../components/Loading";
import { useConfirmModal } from "../hooks/useConfirmModal";
import { useAlertModal } from "../hooks/useAlertModal";
import MyCalendar from "../components/MyCalendar";
import useCalendarEventListModal from "../hooks/useCalendarEventListModal";
import Svg from "../components/figs/Svg";
import useCardTourAndDateModal from "../hooks/useCardTourAndDateModal";
import MultiSelect from "../components/MultiSelect";
import useStaticData from "../hooks/useStaticData";
import ButtonBack from "../components/ButtonBack";
import { useContentModal } from "../hooks/useContentModal";
import QRCode from "qrcode";
import LoadingOnTop from "../components/LoadingOnTop";
import pdfMake from "pdfmake/build/pdfmake";
import { base64 } from "../consts/base64";
import CustomDatePicker from "../components/CustomDatePicker";
import dayjs from "dayjs";

const loadFonts = async () => {
  pdfMake.vfs = {
    ...pdfMake.vfs,
    "Sarabun-Regular.ttf": base64.sarabun.regular,
    "Sarabun-Bold.ttf": base64.sarabun.bold,
    "Sarabun-Italic.ttf": base64.sarabun.italic,
    "Sarabun-BoldItalic.ttf": base64.sarabun.bold_italic,
  };

  pdfMake.fonts = {
    Sarabun: {
      normal: "Sarabun-Regular.ttf",
      bold: "Sarabun-Bold.ttf",
      italics: "Sarabun-Italic.ttf",
      bolditalics: "Sarabun-BoldItalic.ttf",
    },
  };
};

export default function DateManagement() {
  const [isLoading, setIsLoading] = useState(true);
  const { msg } = useSelector((s) => s.language);
  const { staticData } = useStaticData();
  const { tourTypeList, tripDurationList } = staticData;

  const [tourList, setTourList] = useState([]);
  const [tourAndDateList, setTourAndDateList] = useState(null);
  const [boatList, setBoatList] = useState([]);
  const [boatListForForm, setBoatListForForm] = useState([]);
  const [boatTypeList, setBoatTypeList] = useState([]);
  const [destinationList, setDestinationList] = useState([]);
  const [teamList, setTeamList] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [isAddNew, setIsAddNew] = useState(false);
  const [eventList, setEventList] = useState([]);
  const [selectMonthYear, setSelectMonthYear] = useState(new Date());
  const [showButtonBack, setShowButtonBack] = useState(false);

  const [disabledDateList, setDisabledDateList] = useState([]);

  const {
    register,
    setValue,
    trigger,
    handleSubmit,
    watch,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const multiSelectRef = useRef();

  const { confirm, ConfirmModal } = useConfirmModal();
  const { alert, AlertModal } = useAlertModal();
  const { handleOpen, handleClose, setContent, setTitle, ContentModal } =
    useContentModal();
  const {
    handleOpen: handleOpenLoading,
    handleClose: handleCloseLoading,
    setContent: setContentLoading,
    setIsBlank: setIsBlankLoading,
    ContentModal: LoadingModal,
  } = useContentModal();

  const {
    calendarEventList,
    CalendarEventListModal,
    closeCalendarEventListModal,
  } = useCalendarEventListModal();
  const { cardTourAndDate, CardTourAndDateModal, closeCardTourAndDateModal } =
    useCardTourAndDateModal();

  const onChangeMonth = (e) => {
    const temp = e.target.value;
    setSelectMonthYear(new Date(temp));
  };

  const id_trip_duration = watch("id_trip_duration");
  const id_tour_type = watch("id_tour_type");
  const id_boat_list = watch("id_boat_list");
  const id_tour = watch("id_tour");
  const date = watch("date");
  const departure_date = watch("departure_date");
  const boat_team_list = watch("boat_team_list");

  const generateEventList = (tourList, tourAndDateList) => {
    const temp = [];
    tourAndDateList.forEach((el) => {
      const temp2 = {
        ...el,
        ...tourList.find((el2) => +el2.id_tour === +el.id_tour),
      };
      temp.push(temp2);
    });
    setEventList(temp);
  };

  const initFetch = async () => {
    const [
      fetchTourList,
      fetchBoatList,
      fetchTourAndDateList,
      fetchBoatTypeList,
      fetchDestinationList,
      fetchTeamList,
    ] = await Promise.all([
      fetchAuthPost(api.setting.readTourListWithoutImage),
      fetchAuthPost(api.setting.readBoatListWithoutImage),
      fetchAuthPost(api.setting.readTourAndDateList),
      fetchAuthPost(api.setting.readBoatTypeList),
      fetchAuthPost(api.setting.readDestinationList),
      fetchAuthPost(api.setting.readTeamList),
    ]);

    setTourList(fetchTourList.data);
    setBoatList(fetchBoatList.data);
    setTourAndDateList(fetchTourAndDateList.data);
    setBoatTypeList(fetchBoatTypeList.data);
    setDestinationList(fetchDestinationList.data);
    setTeamList(fetchTeamList.data);

    setValue("id_trip_duration", tripDurationList[0].id_trip_duration);
    setValue("id_tour_type", tourTypeList[0].id_tour_type);
    generateEventList(fetchTourList.data, fetchTourAndDateList.data);
  };

  const onAddNew = () => {
    setShowButtonBack(true);
    const now = new Date();
    const yyyymmdd = timeToYYYYMMDD(now.getTime());
    setIsAddNew(true);
    setIsEdit(false);
    setIsView(false);
    reset({
      id_trip_duration: tripDurationList[0].id_trip_duration,
      id_tour_type: tourTypeList[0].id_tour_type,
      date: yyyymmdd,
      id_boat_list: [],
    });
  };

  const onEdit = async (data) => {
    setShowButtonBack(true);
    setIsEdit(true);
    setIsView(false);
    setIsAddNew(false);
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const arrival_date = timeToYYYYMMDD(
      new Date(departure_date).getTime() + night * 24 * 60 * 60 * 1000
    );
    const rev = { ...data, arrival_date };
    reset(rev);
    await fetchAuthPost(api.utils.readDateListByTour, {
      data: {
        id_tour: +getValues("id_tour"),
        id_tour_and_date: +getValues("id_tour_and_date"),
      },
    }).then((r) => {
      setDisabledDateList(r.data);
    });
  };

  const onView = async (data) => {
    setShowButtonBack(true);
    setIsEdit(true);
    setIsView(true);
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const arrival_date = timeToYYYYMMDD(
      new Date(departure_date).getTime() + night * 24 * 60 * 60 * 1000
    );
    const rev = { ...data, arrival_date };
    reset(rev);
    await fetchAuthPost(api.utils.readDateListByTour, {
      data: {
        id_tour: +getValues("id_tour"),
        id_tour_and_date: +getValues("id_tour_and_date"),
      },
    }).then((r) => {
      setDisabledDateList(r.data);
    });
  };

  const onDelete = async (id_tour_and_date) => {
    try {
      const isConfirmDelete = await confirm(
        msg.delete_tour,
        msg.are_you_sure_want_to_delete_this_tour
      );
      if (isConfirmDelete) {
        setIsLoading(true);
        const response = await fetchAuthPost(api.setting.deleteTourAndDate, {
          data: { id_tour_and_date },
        });
        if (response.status === "error") {
          return alert(msg.alert, response.msg);
        }
        const responseTourAndDateList = await fetchAuthPost(
          api.setting.readTourAndDateList
        );
        setTourAndDateList(responseTourAndDateList.data);
      }
    } catch (error) {
    } finally {
      closeCardTourAndDateModal();
      closeCalendarEventListModal();
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    setShowButtonBack(false);
    setIsAddNew(false);
    setIsEdit(false);
    reset({ id_tour });
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      let payload = {
        id_tour_and_date: +data.id_tour_and_date,
        id_tour: +data.id_tour,
        date: data.date,
        id_boat_list: data.id_boat_list,
        description: data.description,
        remark: data.remark,
        departure_date: data.departure_date,
        id_trip_duration: +data.id_trip_duration,
        id_tour_type: +data.id_tour_type,
        boat_team_list: data.boat_team_list,
      };
      if (
        tripDurationList.find(
          (el2) => +el2.id_trip_duration === +id_trip_duration
        ).name === "day_trip"
      ) {
        payload = { ...payload, departure_date: null, arrival_date: null };
      } else if (
        tripDurationList.find(
          (el2) => +el2.id_trip_duration === +id_trip_duration
        ).name === "overnight"
      ) {
      }
      if (data?.id_tour_and_date) {
        const res = await fetchAuthPost(api.setting.updateTourAndDate, {
          data: payload,
        });
        if (res.status === "error") {
          alert(msg.alert, res.msg);
        }
      } else {
        const res = await fetchAuthPost(api.setting.createTourAndDate, {
          data: payload,
        });
        if (res.status === "error") {
          alert(msg.alert, res.msg);
        }
      }
      const response = await fetchAuthPost(api.setting.readTourAndDateList);
      if (response.status === "error") {
        return alert(msg.alert, response.msg);
      }
      setTourAndDateList(response.data);
    } catch (error) {
    } finally {
      setShowButtonBack(false);
      setIsLoading(false);
      setIsAddNew(false);
      setIsEdit(false);
      setIsView(false);
      closeCardTourAndDateModal();
      closeCalendarEventListModal();
      reset({});
    }
  };

  const onReadDataForQrCodeA5 = async ({ id_tour_and_date, id_boat_list }) => {
    handleOpenLoading();
    const data = { id_tour_and_date, id_boat_list };
    const result = await fetchAuthPost(api.utils.readDataForQrCodeA5, { data });
    handleCloseLoading();
    return result.data;
  };

  const onCreateDocDefinition = async ({
    logoAgmBase64WithPrefix,
    logoAgentBase64WithPrefix,
    tourName,
    dateText,
    dateValue,
    tourTypeText,
    tourTypeValue,
    timeText,
    timeValue,
    locationText,
    locationValue,
    teamText,
    contents,
    footerText,
  }) => {
    await loadFonts();
    const content = contents.map((el, index, self) => {
      const temp0 = {
        text: `${teamText} : ${el.team_name}`,
        alignment: "center",
        fontSize: 20,
        bold: true,
        margin: [0, 0, 0, 0],
        color: "#00397d",
      };
      const temp = {
        text: el.boat_name,
        alignment: "center",
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10],
        color: "#00397d",
      };
      const temp2 = {
        image: el.qrcodeBase64,
        width: 320,
        alignment: "center",
      };
      let value;
      if (index !== self.length - 1) {
        value = {
          stack: [temp0, temp, { ...temp2, pageBreak: "after" }],
        };
      } else {
        value = {
          stack: [temp0, temp, temp2],
        };
      }
      return value;
    });
    const docDef = {
      pageSize: "A5",
      pageMargins: [0, 160, 0, 30],
      defaultStyle: {
        font: "Sarabun",
      },

      header: (currentPage, pageCount) => ({
        margin: [45, 30, 45, 0],
        lineHeight: 0.65,
        stack: [
          {
            columnGap: 20,
            columns: [
              {
                image: logoAgmBase64WithPrefix,
                fit: [150, 18], // จำกัดความสูงสูงสุด = 18, กว้างไม่เกิน 150 (แต่คงสัดส่วน)
                alignment: "right",
              },
              {
                image: logoAgentBase64WithPrefix,
                fit: [150, 18],
                alignment: "left",
              },
            ],
          },
          {
            text: tourName,
            alignment: "center",
            color: "#00397d",
            bold: true,
            fontSize: 24,
            margin: [0, 10, 0, 0],
          },
          {
            fontSize: 16,
            columns: [
              {
                width: "*",
                stack: [
                  {
                    text: [
                      { text: `${dateText} : `, bold: true, color: "#00397d" },
                      dateValue,
                    ],
                    margin: [0, 3],
                  },
                  {
                    text: [
                      { text: `${timeText} : `, bold: true, color: "#00397d" },
                      timeValue,
                    ],
                    margin: [0, 3],
                  },
                ],
              },
              {
                width: "*",
                stack: [
                  {
                    text: [
                      {
                        text: `${tourTypeText} : `,
                        bold: true,
                        color: "#00397d",
                      },
                      tourTypeValue,
                    ],
                    margin: [0, 3],
                  },
                  {
                    text: [
                      {
                        text: `${locationText} : `,
                        bold: true,
                        color: "#00397d",
                      },
                      locationValue,
                    ],
                    margin: [0, 3],
                  },
                ],
              },
            ],
            margin: [0, 10, 0, 10],
          },
        ],
      }),

      content,

      footer: {
        text: footerText,
        alignment: "center",
        bold: true,
        margin: [0, 0, 0, 8],
        color: "#00397d",
      },
    };
    return docDef;
  };

  const onClickQrCode = async (data) => {
    try {
      setIsBlankLoading(true);
      setContentLoading(<LoadingOnTop />);
      handleOpenLoading();
      const t = data;
      const tokenQrCodeData = (
        await fetchAuthPost(api.utils.readTokenQrCode, {
          data: {
            id_tour_and_date: t.id_tour_and_date,
            id_boat_list: t.id_boat_list,
          },
        })
      ).data;
      const qrcodeData = [];
      for (const tokenData of tokenQrCodeData) {
        const { id_boat, boat_name, token_qrcode } = tokenData;
        const url = `${process.env.REACT_APP_REGIS_URL}?token=${token_qrcode}`;
        const qrcodeBase64 = await QRCode.toDataURL(url, {
          errorCorrectionLevel: "H", // L, M, Q, H (H คือคุณภาพสูงสุด)
          margin: 0,
          width: 200,
          color: {
            dark: "#000", // สีของจุด
            light: "#FFF", // สีพื้นหลัง
          },
        });
        qrcodeData.push({ id_boat, boat_name, qrcodeBase64 });
      }
      setTitle(t.tour_name);
      const onClickPdf = async (option = "download") => {
        let dataForA5 = await onReadDataForQrCodeA5({
          id_tour_and_date: t.id_tour_and_date,
          id_boat_list: t.id_boat_list,
        });
        const qrcodeBase64List = [];
        for (const el of (await dataForA5).qrcode_data_list) {
          const url = `${process.env.REACT_APP_REGIS_URL}?token=${el.token_qrcode}`;
          const qrcodeBase64 = await QRCode.toDataURL(url, {
            errorCorrectionLevel: "H", // L, M, Q, H (H คือคุณภาพสูงสุด)
            margin: 0,
            width: 320,
            color: {
              dark: "#000", // สีของจุด
              light: "#FFF", // สีพื้นหลัง
            },
          });
          const id_boat = el.id_boat;
          const id_team = t.boat_team_list.find(
            (el2) => `${el2.id_boat}` === `${id_boat}`
          ).id_team;
          const team_name = teamList.find(
            (el2) => `${el2.id_team}` === `${id_team}`
          ).team_name;
          qrcodeBase64List.push({ ...el, qrcodeBase64, team_name, id_team });
        }
        dataForA5 = { ...dataForA5, qrcode_data_list: qrcodeBase64List };
        const docDef = await onCreateDocDefinition({
          logoAgmBase64WithPrefix: `data:image/png;base64,${base64.logoImgJpg}`,
          logoAgentBase64WithPrefix: dataForA5.organization_logo,
          tourName: dataForA5.tour_name,
          dateText: msg.date,
          dateValue: dataForA5?.date || dataForA5?.departure_date,
          tourTypeText: msg.tour_type,
          tourTypeValue: dataForA5.tour_type,
          timeText: msg.time,
          timeValue: `${dataForA5.departure_time} ${msg.to} ${dataForA5.arrival_time}`,
          locationText: msg.location,
          locationValue: dataForA5.destination_list.join(", "),
          teamText: msg.team,
          // teamValue:teamList.find(el=>`${el.id_team}`===`${}`),
          footerText: msg.copyright,
          contents: dataForA5.qrcode_data_list,
        });
        if (option === "print") {
          pdfMake.createPdf(docDef).print();
        }
        if (option === "download") {
          pdfMake
            .createPdf(docDef)
            .download(`${dataForA5.tour_name}__${new Date().getTime()}.pdf`);
        }
      };
      const ContentQrCode = () => (
        <>
          <div className="content-qr-code">
            {qrcodeData.map((el, index) => (
              <div
                className="flex justify-center items-center flex-col gap-[8px]"
                key={index}
              >
                <div className="border p-[16px] rounded-lg">
                  <img
                    src={el.qrcodeBase64}
                    width={200}
                    height={200}
                    alt={el.boat_name}
                  />
                </div>
                <div>{el.boat_name}</div>
              </div>
            ))}
          </div>
          <div className="py-[8px] border-t flex justify-center gap-[8px] container-button-footer-qr">
            <button
              onClick={() => onClickPdf("print")}
              className="_fill_button min-w-[160px] py-[4px] text-sm"
            >
              {msg.print}
            </button>
            <button
              onClick={() => onClickPdf("download")}
              className="_fill_button min-w-[160px] py-[4px] text-sm"
            >
              {msg.download}
            </button>
          </div>
        </>
      );
      setContent(<ContentQrCode />);
      handleOpen();
    } catch (error) {
    } finally {
      handleCloseLoading();
    }
  };

  const onOpenCard = async (data) => {
    setIsBlankLoading(true);
    setContentLoading(<LoadingOnTop />);
    handleOpenLoading();
    try {
      let temp = eventList.find(
        (el) => +el.id_tour_and_date === +data.id_tour_and_date
      );
      const image = await fetchAuthPost(
        api.utils.readImageTourByIdTourAndDate,
        { data: { id_tour_and_date: temp.id_tour_and_date } }
      );
      temp = {
        ...temp,
        boat_name_list: temp.id_boat_list.map(
          (el) => boatList.find((el2) => +el2.id_boat === +el).boat_name
        ),
        destination: temp.id_destination_list.map(
          (el) =>
            destinationList.find((el2) => +el2.id_destination === +el)
              .destination
        ),
        tour_type: tourTypeList.find(
          (el) => +el.id_tour_type === +temp.id_tour_type
        ).tour_type,
        trip_duration: tripDurationList.find(
          (el) => +el.id_trip_duration === +temp.id_trip_duration
        ).trip_duration,
        image: image.data,
      };
      cardTourAndDate(
        <Card
          data={temp}
          onEdit={() => onEdit(temp)}
          onView={() => onView(temp)}
          onDelete={() => onDelete(temp.id_tour_and_date)}
          canEdit={
            new Date(temp.date || temp.departure_date).getTime() +
              (temp.night + 1) * 24 * 60 * 60 * 1000 >
            new Date(timeToYYYYMMDD(new Date().getTime())).getTime()
          }
          canDelete={
            new Date(temp.date || temp.departure_date).getTime() +
              (temp.night + 1) * 24 * 60 * 60 * 1000 >
            new Date(timeToYYYYMMDD(new Date().getTime())).getTime()
          }
          canView={true}
          onClickQrCode={() => onClickQrCode(temp)}
        />
      );
    } catch (error) {
    } finally {
      handleCloseLoading();
    }
  };

  const onOpenEventList = (data) => {
    const filterEvent = eventList
      .filter((el) =>
        data.map((el2) => +el2.id_list).includes(+el.id_tour_and_date)
      )
      .map((el) => ({
        ...el,
        color: data.find((el2) => +el2.id_list === +el.id_tour_and_date)?.color,
      }));

    const content = (
      <div className="w-[30rem] px-4 -my-4 flex flex-col gap-2">
        {filterEvent.map((el) => (
          <div
            onClick={() =>
              onOpenCard({ id_tour_and_date: el.id_tour_and_date })
            }
            key={el.id_tour_and_date}
            className="text-[var(--primary)] flex font-semibold text-[0.9rem] items-center"
          >
            <span className="w-[8rem]">{`${el.departure_time} - ${el.arrival_time}`}</span>
            <span
              className="w-[22rem] px-2 py-1 border-l-4"
              style={{
                borderColor: el.color,
                backgroundColor: `${el.color}55`,
              }}
            >
              {el.tour_name}
            </span>
          </div>
        ))}
      </div>
    );
    calendarEventList(msg["tour_list(s)"], content);
  };

  const onSelectBoat = (data) => {
    setValue(
      "id_boat_list",
      data.map((el) => +el.value)
    );
  };

  const onAddNewTeam = async (teamName) => {
    const res = await fetchAuthPost(api.setting.createTeam, {
      data: { team_name: teamName },
    });
    if (res.status === "success") {
      const fetchTeamList = await fetchAuthPost(api.setting.readTeamList);
      setTeamList(fetchTeamList.data);
    } else {
      alert(msg.alert, res.msg);
    }
  };

  const onSelectTeamOption = (data, index) => {
    setValue(`boat_team_list.${index}`, {
      id_boat: +(boatList || []).find(
        (el) => +el.id_boat === +id_boat_list[index]
      ).id_boat,
      id_team: (data || []).map((el) => el.value)[0] || null,
    });
    if (boat_team_list[index]?.id_team) {
      trigger(`boat_team_list.${index}`);
    }
  };

  const generateEventEachDate = () => {
    const temp = [];
    for (let i = 0; i < eventList.length; i++) {
      const element = eventList[i];

      const id_tour = element.id_tour;
      const id_tour_and_date = element.id_tour_and_date;

      const departure_time = element.departure_time;
      const arrival_time = element.arrival_time;
      const departure_time_number = hhmm_to_number_sec(departure_time);
      const arrival_time_number = hhmm_to_number_sec(arrival_time);

      const start_date = element.date || element.departure_date;
      const start_date_getTime = new Date(start_date).getTime();
      const night = element.night || 0;
      for (let j = 0; j < night + 1; j++) {
        let temp3 = start_date_getTime + j * 24 * 60 * 60 * 1000;
        const temp_date = timeToYYYYMMDD(temp3);
        const temp4 = {
          active_date: temp_date,
          departure_time,
          departure_time_number,
          arrival_time,
          arrival_time_number,
          id_tour,
          id_tour_and_date,
        };
        temp.push(temp4);
      }
    }
    return temp;
  };

  const filterForSelectBoatList = () => {
    const eventEachDate = generateEventEachDate();
    const current_id_tour = +getValues("id_tour");
    const current_tour = tourList.find(
      (el) => el.id_tour + "" === current_id_tour + ""
    );
    const current_night = +(current_tour?.night || 0);

    const current_id_tour_and_date = +getValues("id_tour_and_date");

    const currentSelect_date = date;
    const currentSelect_departure_date = departure_date;
    const currentSelect_active_date = date || departure_date;
    const currentSelect_active_date_list = [];
    for (let i = 0; i < current_night + 1; i++) {
      const temp = timeToYYYYMMDD(
        new Date(currentSelect_active_date).getTime() + i * 24 * 60 * 60 * 1000
      );
      currentSelect_active_date_list.push(temp);
    }

    // นำ event ของ current_id_tour_and_date ออก
    const newEventEachDate = eventEachDate.filter(
      (el) => `${el.id_tour_and_date}` !== `${current_id_tour_and_date}`
    );

    // เอา id_tour_and_date ภายในกรอบวันมาพิจารณา id_boat ที่อณุญาตให้ใช้ได้
    // สร้าง list [{id_tour_and_date, id_tour, departure_time, arrival_time, id_boat},...]
    // ต้องพิจารณาเรื่องเวลาของทัวร์ไม่เต็มวันทำให้อาจใช้เรือเดิมหลายรอบในวันเดียวได้
    const newEventEachDate_add_idBoatList = [];
    for (let i = 0; i < newEventEachDate.length; i++) {
      const element = newEventEachDate[i];
      const tempIdTourAndDate = element.id_tour_and_date;
      const tempIdBoatList =
        tourAndDateList.find(
          (el) => `${el.id_tour_and_date}` === `${tempIdTourAndDate}`
        )?.id_boat_list || [];
      newEventEachDate_add_idBoatList.push({
        ...element,
        id_boat_list: tempIdBoatList,
      });
    }

    const eventInInterval = [];
    for (const element of newEventEachDate_add_idBoatList) {
      if (currentSelect_active_date_list.includes(element.active_date)) {
        eventInInterval.push(element);
      }
    }

    // const currentTourData = tourList.find(
    //   (el) => +el.id_tour === +current_id_tour
    // );

    // กรองเอาเฉพาะเรือที่ว่างใน slot เวลาของทัวร์ที่เราเลือก
    // เรือในทัวร์อื่นที่ time slot ซ้อนกับทัวร์ที่เรากำลังเพิ่มหรือแก้ไข --> จะได้รายการเรือที่ไม่ให้แสดงใน select boat
    let tempIdBoatNotAllow = [];
    const current_departure_time_number = hhmm_to_number_sec(
      current_tour?.departure_time || "00:00"
    );
    const current_arrival_time_number = hhmm_to_number_sec(
      current_tour?.arrival_time || "00:00"
    );
    for (const element of eventInInterval) {
      const temp_departure_time_number = hhmm_to_number_sec(
        element?.departure_time
      );
      const temp_arrival_time_number = hhmm_to_number_sec(
        element?.arrival_time
      );
      // เวลาซ้อนกัน
      const en = enRatio(
        current_departure_time_number,
        current_arrival_time_number,
        temp_departure_time_number,
        temp_arrival_time_number
      );
      const isOverlap = en < 1;
      if (isOverlap) {
        tempIdBoatNotAllow.push(...element.id_boat_list);
      }
    }

    tempIdBoatNotAllow = [...new Set(tempIdBoatNotAllow)];

    const boatThatAllowSelect = boatList.filter(
      (el) => !tempIdBoatNotAllow.includes(el.id_boat)
    );

    // setBoatListForForm(boatThatAllowSelect);
    return boatThatAllowSelect;
  };

  useEffect(() => {
    initFetch();
    setValue("id_boat_list", []);
  }, []);
  useEffect(() => {
    if (isEdit || isView) {
      setValue("id_trip_duration", id_trip_duration);
      setValue("id_tour_type", id_tour_type);
    }
    const boatThatAllowSelect = filterForSelectBoatList();
    setBoatListForForm(boatThatAllowSelect);
  }, [isEdit, isView]);
  const onChangeTripDuration = (event) => {
    const id_trip_duration = +event.target.value;
    setValue("id_tour", null);
    setValue("id_boat_list", []);
    multiSelectRef.current?.clearSelectedOption();
    const now = new Date(date || departure_date) || new Date();

    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const start_yyyymmdd = timeToYYYYMMDD(now.getTime());
    const end_yyyymmdd = timeToYYYYMMDD(
      now.getTime() + night * 24 * 60 * 60 * 1000
    );
    if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "overnight"
    ) {
      setValue("date", null);
      setValue("departure_date", start_yyyymmdd);
      setValue("arrival_date", end_yyyymmdd);
    } else if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "day_trip"
    ) {
      setValue("date", start_yyyymmdd);
      setValue("departure_date", null);
      setValue("arrival_date", null);
    }
  };
  useEffect(() => {
    if (tourAndDateList?.length > 0) {
      generateEventList(tourList, tourAndDateList);
    } else {
      setEventList([]);
    }
  }, [tourAndDateList]);
  useEffect(() => {
    if (id_boat_list?.length > 0) {
      trigger("id_boat_list");
    }
    const temp = (boat_team_list || []).filter((el) =>
      id_boat_list.includes(el.id_boat)
    );
    setValue("boat_team_list", temp);
  }, [id_boat_list]);
  useEffect(() => {
    const now = new Date(date || departure_date) || new Date();
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const start_yyyymmdd = timeToYYYYMMDD(now.getTime());
    const end_yyyymmdd = timeToYYYYMMDD(
      now.getTime() + night * 24 * 60 * 60 * 1000
    );
    if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "overnight"
    ) {
      setValue("date", null);
      setValue("departure_date", start_yyyymmdd);
      setValue("arrival_date", end_yyyymmdd);
    } else if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "day_trip"
    ) {
      setValue("date", start_yyyymmdd);
      setValue("departure_date", null);
      setValue("arrival_date", null);
    }
  }, [id_tour]);
  const onChangeTour = async () => {
    setIsLoading(true);
    const now = new Date(date || departure_date) || new Date();
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const start_yyyymmdd = timeToYYYYMMDD(now.getTime());
    const end_yyyymmdd = timeToYYYYMMDD(
      now.getTime() + night * 24 * 60 * 60 * 1000
    );
    await fetchAuthPost(api.setting.readBoatListAvailableWithoutImage, {
      start_date: start_yyyymmdd,
      end_date: end_yyyymmdd,
      id_tour: +getValues("id_tour"),
      id_tour_and_date: +getValues("id_tour_and_date"),
    }).then((r) => {
      if (r.status === "success") {
        // const boatThatAllowSelect = filterForSelectBoatList();
        // console.log(boatThatAllowSelect, r.data);
        setBoatListForForm(r.data);
      }
    });
    await fetchAuthPost(api.utils.readDateListByTour, {
      data: {
        id_tour: +getValues("id_tour"),
        id_tour_and_date: +getValues("id_tour_and_date"),
      },
    }).then((r) => {
      setDisabledDateList(r.data);
    });
    if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "overnight"
    ) {
      setValue("date", null);
      setValue("departure_date", start_yyyymmdd);
      setValue("arrival_date", end_yyyymmdd);
    } else if (
      tripDurationList.find(
        (el2) => +el2.id_trip_duration === +id_trip_duration
      )?.name === "day_trip"
    ) {
      setValue("date", start_yyyymmdd);
      setValue("departure_date", null);
      setValue("arrival_date", null);
    }
    setValue("id_boat_list", []);
    multiSelectRef.current?.clearSelectedOption();
    setIsLoading(false);
  };
  useEffect(() => {
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    setValue(
      "arrival_date",
      timeToYYYYMMDD(
        new Date(departure_date).getTime() + night * 24 * 60 * 60 * 1000
      )
    );
  }, [departure_date]);
  const onChangeDate = async (event) => {
    const value = event.target.value;
    let temp;
    if (new Date(value).getTime() < new Date().getTime()) {
      temp = timeToYYYYMMDD(new Date().getTime());
      setValue("date", temp);
    } else {
      temp = timeToYYYYMMDD(new Date(value).getTime());
      setValue("date", temp);
    }
    if (value === "") {
      temp = timeToYYYYMMDD(new Date().getTime());
      setValue("date", temp);
    }
    setIsLoading(true);
    await fetchAuthPost(api.setting.readBoatListAvailableWithoutImage, {
      start_date: temp,
      end_date: temp,
      id_tour: +getValues("id_tour"),
      id_tour_and_date: +getValues("id_tour_and_date"),
    }).then((r) => {
      if (r.status === "success") {
        // const boatThatAllowSelect = filterForSelectBoatList();
        // console.log(boatThatAllowSelect, r.data);
        setBoatListForForm(r.data);
      }
    });
    setValue("id_boat_list", []);
    multiSelectRef.current?.clearSelectedOption();
    setIsLoading(false);
  };
  const onChangeDepartureDate = async (event) => {
    const value = event.target.value;
    let temp;
    if (new Date(value).getTime() < new Date().getTime()) {
      temp = timeToYYYYMMDD(new Date().getTime());
      setValue("departure_date", temp);
    } else {
      temp = timeToYYYYMMDD(new Date(value).getTime());
      setValue("departure_date", temp);
    }
    if (value === "") {
      temp = timeToYYYYMMDD(new Date().getTime());
      setValue("departure_date", temp);
    }
    const night = +(
      tourList.find((el) => +el.id_tour === +getValues("id_tour"))?.night || 0
    );
    const arrival_date = timeToYYYYMMDD(
      new Date(temp).getTime() + night * 24 * 60 * 60 * 1000
    );
    setIsLoading(true);
    await fetchAuthPost(api.setting.readBoatListAvailableWithoutImage, {
      start_date: temp,
      end_date: arrival_date,
      id_tour: +getValues("id_tour"),
      id_tour_and_date: +getValues("id_tour_and_date"),
    }).then((r) => {
      if (r.status === "success") {
        // const boatThatAllowSelect = filterForSelectBoatList();
        // console.log(boatThatAllowSelect, r.data);
        setBoatListForForm(r.data);
      }
    });
    setValue("id_boat_list", []);
    multiSelectRef.current?.clearSelectedOption();
    setIsLoading(false);
  };
  useEffect(() => {
    if (
      tourList?.length >= 0 &&
      boatList?.length >= 0 &&
      tourAndDateList?.length >= 0 &&
      eventList?.length >= 0 &&
      boatTypeList?.length > 0 &&
      destinationList?.length >= 0 &&
      id_boat_list >= 0
    ) {
      setIsLoading(false);
    }
  }, [
    tourList,
    boatList,
    tourAndDateList,
    eventList,
    boatTypeList,
    destinationList,
    id_boat_list,
  ]);
  if (isLoading) return <Loading />;
  return (
    <>
      <div className="page_date_management">
        <ButtonBack
          hidden={!showButtonBack}
          onClick={() => {
            onCancel();
          }}
        />
        <HeaderLogo />
        <PageName titleName={msg.date_management} />
        {isAddNew === false && isEdit === false && (
          <div className="calendar">
            <div className="headbar">
              <div>
                {
                  [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ][selectMonthYear.getMonth()]
                }{" "}
                {selectMonthYear.getFullYear()}
              </div>
              <div>
                <span>
                  <button
                    onClick={() =>
                      setSelectMonthYear(
                        (cur) =>
                          new Date(cur.getFullYear(), cur.getMonth() - 1, 1)
                      )
                    }
                  >
                    {"<"}
                  </button>
                  <div>
                    <input
                      value={timeToYYYYMM(selectMonthYear.getTime())}
                      type="month"
                      onChange={onChangeMonth}
                    />
                  </div>
                  <button
                    onClick={() =>
                      setSelectMonthYear(
                        (cur) =>
                          new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
                      )
                    }
                  >
                    {">"}
                  </button>
                </span>
                <button onClick={onAddNew}>
                  <Svg iconName="fill_add" />
                </button>
              </div>
            </div>
            <MyCalendar
              inputNumOfRow={6}
              inputYear={selectMonthYear.getFullYear()}
              inputMonth={selectMonthYear.getMonth()}
              onOpenEventList={onOpenEventList}
              eventList={eventList.map((el) => ({
                id_list: el.id_tour_and_date,
                start_date: el.date || el.departure_date,
                end_date:
                  el.date ||
                  timeToYYYYMMDD(
                    new Date(el.departure_date).getTime() +
                      +(el?.night || 0) * 24 * 60 * 60 * 1000
                  ),
                start_time: el.departure_time,
                end_time: el.arrival_time,
                title: tourList.find((el2) => +el2.id_tour === +el.id_tour)
                  .tour_name,
                onClick: () => {
                  onOpenCard({ id_tour_and_date: el.id_tour_and_date });
                },
              }))}
            />
          </div>
        )}
        {(isAddNew || isEdit) && (
          <>
            <div className="date_management_form">
              <div className="_title_name_form">{msg.add_date_tour}</div>
              <div className="date_tour_form_body">
                <div className="section_radio">
                  <div>
                    <div>{`${msg.trip_duration} *`}</div>
                    <div>
                      {tripDurationList.map((el) => (
                        <div key={el.id_trip_duration} className="_input_radio">
                          <input
                            type="radio"
                            {...register("id_trip_duration", {
                              required: true,
                              // onChange: () => {
                              //   setValue("id_tour", null);
                              // },
                              onChange: onChangeTripDuration,
                            })}
                            value={+el.id_trip_duration}
                            checked={+id_trip_duration === +el.id_trip_duration}
                            disabled={isView}
                          />
                          <label>{el.trip_duration}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div>{`${msg.tour_type} *`}</div>
                    <div>
                      {tourTypeList.map((el) => (
                        <div key={el.id_tour_type} className="_input_radio">
                          <input
                            type="radio"
                            {...register("id_tour_type", { required: true })}
                            value={+el.id_tour_type}
                            checked={+id_tour_type === +el.id_tour_type}
                            disabled={isView}
                          />
                          <label>{el.tour_type}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="_input_text_select">
                  <label>{`${msg.tour_name} *`}</label>
                  <select
                    {...register("id_tour", {
                      required: true,
                      onChange: onChangeTour,
                    })}
                    className={errors.id_tour ? "_data_invalid" : ""}
                    disabled={isView}
                  >
                    <>
                      <option value="" label={msg.tour_name} />
                      {tourList
                        .filter(
                          (el) => +el.id_trip_duration === +id_trip_duration
                        )
                        .map((el) => (
                          <option
                            key={+el.id_tour}
                            value={+el.id_tour}
                            label={el.tour_name}
                          />
                        ))}
                    </>
                  </select>
                </div>
                {tripDurationList.find(
                  (el2) => +el2.id_trip_duration === +id_trip_duration
                )?.name === "day_trip" && (
                  <div className="case_day_trip">
                    <div className="_input_text_select">
                      <label>{`${msg.date} *`}</label>
                      {/* <input
                        type="date"
                        min={timeToYYYYMMDD(new Date().getTime())}
                        placeholder={msg.date}
                        {...register("date", {
                          required: true,
                          onChange: onChangeDate,
                        })}
                        className={errors.date ? "_data_invalid" : ""}
                        disabled={isView || !id_tour}
                      /> */}
                      <Controller
                        control={control}
                        name="date"
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                          <CustomDatePicker
                            className={fieldState.error ? "_data_invalid" : ""}
                            disabled={isView || !id_tour}
                            onChange={(date) => {
                              onChangeDate({ target: { value: date } });
                              field.onChange(date);
                            }}
                            value={
                              field.value &&
                              !disabledDateList.includes(field.value)
                                ? dayjs(field.value)
                                : null
                            }
                            disabledDateFunction={(current) => {
                              const temp =
                                current &&
                                (disabledDateList.includes(
                                  current.format("YYYY-MM-DD")
                                ) ||
                                  current <
                                    dayjs(
                                      timeToYYYYMMDD(new Date().getTime()),
                                      "YYYY-MM-DD"
                                    ));
                              return temp;
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}

                {tripDurationList.find(
                  (el2) => +el2.id_trip_duration === +id_trip_duration
                )?.name === "overnight" && (
                  <div className="case_overnight">
                    <div className="_input_text_select">
                      <label>{`${msg.departure_date} *`}</label>
                      {/* <input
                        type="date"
                        min={timeToYYYYMMDD(new Date().getTime())}
                        placeholder={msg.departure_date}
                        {...register("departure_date", {
                          required: true,
                          onChange: onChangeDepartureDate,
                        })}
                        className={errors.departure_date ? "_data_invalid" : ""}
                        disabled={isView || !id_tour}
                      /> */}
                      <Controller
                        control={control}
                        name="departure_date"
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                          <CustomDatePicker
                            className={fieldState.error ? "_data_invalid" : ""}
                            disabled={isView || !id_tour}
                            onChange={(date) => {
                              onChangeDepartureDate({
                                target: { value: date },
                              });
                              field.onChange(date);
                            }}
                            value={
                              field.value &&
                              !disabledDateList.includes(field.value)
                                ? dayjs(field.value)
                                : null
                            }
                            disabledDateFunction={(current) => {
                              const temp =
                                current &&
                                (disabledDateList.includes(
                                  current.format("YYYY-MM-DD")
                                ) ||
                                  current <
                                    dayjs(
                                      timeToYYYYMMDD(new Date().getTime()),
                                      "YYYY-MM-DD"
                                    ));
                              return temp;
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className="_input_text_select">
                      <label>{`${msg.arrival_date} *`}</label>
                      {/* <input
                        type="date"
                        placeholder={msg.arrival_date}
                        {...register("arrival_date")}
                        disabled
                      /> */}
                      <Controller
                        control={control}
                        name="arrival_date"
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                          <CustomDatePicker
                            className={fieldState.error ? "_data_invalid" : ""}
                            disabled
                            value={
                              field.value &&
                              !disabledDateList.includes(field.value)
                                ? dayjs(field.value)
                                : null
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                )}

                {id_boat_list.length >= 0 && (
                  <div className="_input_text_select">
                    <label>{`${msg.boat_name} *`}</label>
                    <input
                      type="hidden"
                      {...register("id_boat_list", { required: true })}
                    />
                    <MultiSelect
                      className={errors.id_boat_list ? "_data_invalid" : ""}
                      disabled={isView || boatList.length === 0 || !id_tour}
                      placeholder={msg.boat_name}
                      disabledAddNewOption
                      onSelectOption={onSelectBoat}
                      selectedOption={id_boat_list.map((el) => ({
                        value: el,
                        label: boatList.find((el2) => +el2.id_boat === +el)
                          .boat_name,
                      }))}
                      options={boatListForForm.map((el) => ({
                        value: el.id_boat,
                        label: el.boat_name,
                      }))}
                      ref={multiSelectRef}
                    />
                    {/* {SelectBoat} */}
                  </div>
                )}
                {id_boat_list.length > 0 && (
                  <div>
                    <div className="_input_text_select">
                      <label>{`${msg.team_name} *`}</label>
                    </div>
                    <div className="container-select-team">
                      {id_boat_list.map((el, index) => (
                        <div
                          key={el}
                          className="_input_text_select block-select-team"
                        >
                          <input
                            type="hidden"
                            {...register(`boat_team_list.${index}.id_team`, {
                              required: true,
                            })}
                          />
                          <label>
                            {`${index + 1}. `}
                            {boatList.find((el2) => +el2.id_boat === +el)
                              ?.boat_name || ""}
                          </label>
                          <MultiSelect
                            className={
                              errors?.boat_team_list?.[index]?.id_team
                                ? "_data_invalid"
                                : ""
                            }
                            disabled={isView}
                            placeholder={msg.team}
                            multiple={false}
                            onSaveNewOption={onAddNewTeam}
                            onSelectOption={(data) =>
                              onSelectTeamOption(data, index)
                            }
                            options={teamList
                              .map((el2) => ({
                                value: el2.id_team,
                                label: el2.team_name,
                              }))
                              .filter(
                                (el2) =>
                                  !boat_team_list
                                    .map((el3) => el3.id_team)
                                    .includes(el2.value)
                              )}
                            selectedOption={(teamList || [])
                              .filter(
                                (el2) =>
                                  el2?.id_team ===
                                  (boat_team_list || []).find(
                                    (el3) =>
                                      el3?.id_team === el2?.id_team &&
                                      el3?.id_boat === el
                                  )?.id_team
                              )
                              .map((el2) => ({
                                value: el2.id_team,
                                label: el2.team_name,
                              }))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="_input_text_select">
                  <label>{`${msg.description}`}</label>
                  <textarea
                    placeholder={msg.description}
                    {...register("description")}
                    className={errors.description ? "_data_invalid" : ""}
                    disabled={isView}
                  />
                </div>
                <div className="_input_text_select">
                  <label>{`${msg.remark}`}</label>
                  <textarea
                    placeholder={msg.remark}
                    {...register("remark")}
                    className={errors.remark ? "_data_invalid" : ""}
                    disabled={isView}
                  />
                </div>
                {new Date(
                  getValues("date") || getValues("departure_date")
                ).getTime() >=
                  new Date(timeToYYYYMMDD(new Date().getTime())).getTime() && (
                  <div className="date_management_button">
                    {isView ? (
                      <button
                        onClick={() => {
                          if (
                            new Date(
                              getValues("date") || getValues("departure_date")
                            ).getTime() >=
                            new Date(
                              timeToYYYYMMDD(new Date().getTime())
                            ).getTime()
                          )
                            setIsView(false);
                        }}
                        className={
                          new Date(
                            getValues("date") || getValues("departure_date")
                          ).getTime() >=
                          new Date(
                            timeToYYYYMMDD(new Date().getTime())
                          ).getTime()
                            ? "_fill_button"
                            : "_fill_button disabled"
                        }
                      >
                        {msg.edit}
                      </button>
                    ) : (
                      <>
                        <button className="_outline_button" onClick={onCancel}>
                          {msg.cancel}
                        </button>
                        <button
                          onClick={handleSubmit(onSubmit)}
                          className="_fill_button"
                        >
                          {getValues("id_tour_and_date")
                            ? msg.update
                            : msg.save}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {isAddNew === false && isEdit === false && (
          <>
            {CalendarEventListModal}
            {CardTourAndDateModal}
          </>
        )}
        {ConfirmModal}
        {AlertModal}
        {ContentModal}
      </div>
      {LoadingModal}
    </>
  );
}

const Card = ({
  data,
  onEdit,
  onDelete,
  onView,
  canEdit,
  canView,
  canDelete,
  onClickQrCode = (e) => {},
}) => {
  const { msg } = useSelector((s) => s.language);
  const t = data;
  return (
    <div className="card_tour_and_date">
      <div
        onClick={canView ? onView : () => {}}
        className={canView ? "image_block" : "image_block cursor-not-allowed"}
      >
        {t?.image ? (
          <img src={t.image} alt={t.tour_name} />
        ) : (
          <Svg iconName="empty_image" />
        )}
      </div>
      <div className="trip_duration">{t.trip_duration}</div>
      <h2>{t.tour_name}</h2>
      <div className="section_boat_name_list">
        <div className="boat_name_list">
          <div>{`${msg.boat_name} :`}</div>
          {t.boat_name_list.map((el, index, self) => (
            <span key={index}>{`${el}${
              index < self.length - 1 ? "," : ""
            }`}</span>
          ))}
        </div>
        <div className="boat_name_tooltip">
          {t.boat_name_list.map((el, index) => (
            <div key={index}>
              <div className="bullet"></div>
              <div>{el}</div>
            </div>
          ))}
        </div>
      </div>
      <p>{t.description || "-"}</p>
      <div className="list">
        <div>
          <Svg iconName="gray_calendar" />
          <span>
            {t.night
              ? `${yyyymmddd_ddmmyyyy_v1(t.departure_date)} ${
                  msg.to
                } ${yyyymmddd_ddmmyyyy_v1(
                  timeToYYYYMMDD(
                    new Date(t.departure_date).getTime() +
                      t.night * 24 * 60 * 60 * 1000
                  )
                )}, ${t.departure_time} ${msg.to} ${t.arrival_time}`
              : `${yyyymmddd_ddmmyyyy_v1(t.date)}, ${t.departure_time} ${
                  msg.to
                } ${t.arrival_time}`}
          </span>
        </div>
        <div>
          <Svg iconName="bag" />
          <span>{t.tour_type}</span>
        </div>
        <div>
          <Svg iconName="gray_location" />
          <span>{t.destination.join(", ")}</span>
        </div>
      </div>
      <div className="remark">{`${msg.remark}: ${t.remark || "-"}`}</div>
      <div className="qrcode">
        <div onClick={onClickQrCode}>
          <Svg iconName="qrcode" />
        </div>
      </div>
      {new Date(t.departure_date || t.date).getTime() +
        (t.night + 1) * 24 * 60 * 60 * 1000 >
      new Date(timeToYYYYMMDD(new Date().getTime())).getTime() ? (
        <div className="button">
          <button
            onClick={canEdit ? onEdit : () => {}}
            className={canEdit ? "" : "cursor-not-allowed"}
          >
            <Svg iconName="edit" />
            <span>{msg.edit}</span>
          </button>
          <div></div>
          <button
            onClick={canDelete ? onDelete : () => {}}
            className={canDelete ? "" : "cursor-not-allowed"}
          >
            <Svg iconName="delete" />
            <span>{msg.delete}</span>
          </button>
        </div>
      ) : (
        <div style={{ height: 10 }} />
      )}
    </div>
  );
};
